import { Center, FormControl, FormLabel, Switch } from "@chakra-ui/react";
import React from "react";
import { Navigate } from "react-router-dom";

import {
  Alert,
  LoadingIndicator,
  SettingsPageContainer,
} from "../../../../components";
import { CurrentUserFragment } from "../../../graphql";
import useRediscoveryTask from "./useRediscoveryTask";
import useUserCanAccessRediscovery from "./useUserCanAccessRediscovery";

interface CandidateRediscoverySettingsProps {
  currentUser: CurrentUserFragment;
}

const CandidateRediscoverySettings: React.FC<
  CandidateRediscoverySettingsProps
> = ({ currentUser }) => {
  const {
    taskDefinition,
    createRediscoveryTaskDefinition,
    deleteRediscoveryTaskDefinition,
  } = useRediscoveryTask();
  const taskDefinitionId = taskDefinition.taskDefinition?.id;
  const isEnabled = !!taskDefinitionId;

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { checked } = e.target;
    if (checked) {
      createRediscoveryTaskDefinition.mutation();
    } else {
      deleteRediscoveryTaskDefinition.mutation();
    }
  };

  const userCanAccessRediscovery = useUserCanAccessRediscovery(currentUser);
  if (!userCanAccessRediscovery) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <SettingsPageContainer
      maxW={660}
      heading="Candidate Rediscovery"
      subHeading="Candidate Rediscovery helps you re-connect with high-potential talent. Interview transcripts and resumes from past applicants are used to identify potential candidates for open positions."
    >
      <FormControl display="flex" alignItems="center" mb={6}>
        <FormLabel htmlFor="rediscovery-switch" mb="0">
          Enable Candidate Rediscovery
        </FormLabel>
        <Switch
          id="rediscovery-switch"
          isChecked={isEnabled}
          isDisabled={
            taskDefinition.loading ||
            createRediscoveryTaskDefinition.loading ||
            deleteRediscoveryTaskDefinition.loading
          }
          onChange={handleToggle}
        />
      </FormControl>
      {taskDefinition.loading && (
        <Center>
          <LoadingIndicator />
        </Center>
      )}
      {!taskDefinition.loading && taskDefinition.error && (
        <Alert
          status="error"
          title="Error fetching Candidate Rediscovery information"
          description={taskDefinition.error.message}
        />
      )}
    </SettingsPageContainer>
  );
};

export default CandidateRediscoverySettings;
